<template>
    <div id="phase-detail">
        <div>
            <q-btn
                @click="closeDetail"
                :label="$t('Return to asset timeline')"
                class="q-mb-sm return-button"
                icon="fas fa-chevron-left"
                flat
            ></q-btn>
        </div>

        <q-card class="timeline-panel__form">
            <div class="timeline-panel__toolbar">
                <q-bar class="bg-primary text-white text-weight-medium">
                    {{ $t('Phase Detail') }}
                </q-bar>
            </div>

            <q-form
                @submit="submit"
                :style="$q.screen.lt.md ? 'width: 100%' : 'width: 50%'"
                class="q-pa-md"
                style="margin: 0 auto"
            >
                <banner
                    v-if="!end"
                    class="q-mb-md"
                    :is-visible="!end"
                    type="info"
                    :message="$t(editPhaseInfoMessage)"
                />
                <date-time-picker
                    v-model="form.start"
                    :label="$t('Start')"
                    :disable="true"
                    class="q-mb-sm"
                />
                <date-time-picker
                    v-model="form.end"
                    :label="$t('End')"
                    :disable="true"
                    class="q-mb-sm"
                />
                <q-select
                    id="status-select"
                    v-model="form.status"
                    :label="$t('Status')"
                    :options="statusOptions"
                    class="q-mb-lg full-width"
                    outlined
                    lazy-rules
                    :disable="!isPhaseEditable(end) || isPhaseInProgress(end)"
                    color="accent"
                >
                    <!-- Option style -->
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on:click="scope.itemEvents"
                        >
                            <q-item-section class="row">
                                <div>
                                    <q-icon
                                        :color="
                                            ASSET_STATUS_TO_STYLE[
                                                scope.opt.value
                                            ]['color']
                                        "
                                        name="fas fa-circle"
                                        class="q-mr-sm"
                                    ></q-icon>
                                    {{ $t(scope.opt.label) }}
                                </div>
                            </q-item-section>
                        </q-item>
                    </template>
                    <!-- Selected item style -->
                    <template v-slot:selected>
                        <q-icon
                            :color="form.status['color']"
                            style="margin-bottom: 2px"
                            name="fas fa-circle"
                        ></q-icon>
                        <div style="margin-left: 5px">
                            {{ $t(form.status['label']) }}
                        </div>
                    </template>
                </q-select>

                <!-- Product select -->
                <table-select
                    id="product-select"
                    v-if="form.status.value !== 'stopped'"
                    v-model="form.product"
                    @confirmSelection="
                        form.product = productSelectProps.selection
                    "
                    :baseURL="productSelectProps.url"
                    :queryParams="productSelectProps.queryParams"
                    :label="productSelectProps.label"
                    :dialogTitle="productSelectProps.dialogTitle"
                    :searchLabel="productSelectProps.searchLabel"
                    :rules="formRules.product"
                    row-key="id"
                    class="q-mb-sm"
                    :disable="!isPhaseEditable(end) || isPhaseInProgress(end)"
                >
                    <template v-slot:item="data">
                        <product-card
                            @select="
                                productSelectProps.selection = {
                                    label: data.props.row.name,
                                    value: data.props.row.product
                                }
                            "
                            :name="data.props.row.name"
                            :code="data.props.row.code"
                            :description="data.props.row.description"
                            :unit-measure="data.props.row.unit_measure"
                            :unit-packing="data.props.row.unit_packing"
                            :isSelected="
                                productSelectProps.selection &&
                                productSelectProps.selection.value ===
                                    data.props.row.product
                            "
                            :isSelectable="true"
                        ></product-card>
                    </template>
                </table-select>

                <!-- Cause select -->
                <table-select
                    id="cause-select"
                    v-if="
                        form.status.value === 'changeover' ||
                        form.status.value === 'in_downtime' ||
                        form.status.value === 'stopped'
                    "
                    v-model="form.cause"
                    @confirmSelection="form.cause = causeSelectProps.selection"
                    :baseURL="causeSelectProps.url"
                    :queryParams="getCauseQueryParams"
                    :label="causeSelectProps.label"
                    :dialogTitle="causeSelectProps.dialogTitle"
                    :searchLabel="causeSelectProps.searchLabel"
                    :rules="formRules.cause"
                    row-key="id"
                    class="q-mb-sm"
                    :disable="!isPhaseEditable(end)"
                    :no-data-label="getNoShutdownCausesMessage"
                >
                    <template v-slot:item="data">
                        <cause-card
                            @select="
                                causeSelectProps.selection = {
                                    label: data.props.row.name,
                                    value: data.props.row.id
                                }
                            "
                            :name="data.props.row.name"
                            :lossType="data.props.row.type_of_loss"
                            :categoryName="data.props.row.category_name"
                            :planned="data.props.row.planned"
                            :description="data.props.row.description"
                            :targetDowntime="data.props.row.target_down_time"
                            :isSelected="
                                causeSelectProps.selection &&
                                causeSelectProps.selection.value ===
                                    data.props.row.id
                            "
                            :isSelectable="true"
                            :equipmentFailure="data.props.row.equipment_failure"
                        ></cause-card>
                    </template>
                </table-select>
                <!-- Proximity Sensor options dropdown -->
                <q-select
                    v-if="
                        form.status.value === 'in_downtime' &&
                        hasProximitySensor &&
                        proximityReading !== null
                    "
                    v-model="form.proximityReading"
                    :options="proximitySensorOptions"
                    :label="$t('Product on line?')"
                    :rules="[
                        val =>
                            (val !== '' && val !== null) ||
                            $t('Select an Option')
                    ]"
                    class="q-mb-sm"
                    behavior="dialog"
                    outlined
                    emit-value
                    map-options
                    color="accent"
                />

                <!-- Description field -->
                <q-input
                    data-test="phase-description"
                    v-model="form.description"
                    :label="$t('Description')"
                    :input-style="'min-height: 45px; max-height: 90px;'"
                    type="textarea"
                    class="q-mb-md"
                    outlined
                    color="accent"
                    :disable="!isPhaseEditable(end)"
                />

                <!-- Media -->
                <div
                    v-if="isMediaUploadEnabled"
                    class="row q-mt-md q-mr-md q-gutter-md"
                >
                    <div
                        class="col-sm-1 col-xs-12"
                        :class="{ 'q-mb-md': $q.screen.lt.sm }"
                    >
                        <!-- Add media -->
                        <media-input-manager
                            :mediaCount="
                                editableMedia.length +
                                updatedMedia.add.length -
                                updatedMedia.remove.length
                            "
                            @update:add="
                                medias => updatedMedia.add.push(...medias)
                            "
                        />
                    </div>
                    <div class="col-sm-10 col-xs-12">
                        <!-- Display media with remove button -->
                        <media-grid-manager
                            :phaseId="phaseId"
                            :existingMedias="phaseMedia"
                            :newMedias="updatedMedia.add"
                            @update:removeExistingMedia="
                                mediaId => updatedMedia.remove.push(mediaId)
                            "
                            @update:removeNewMedia="
                                newMedias => (updatedMedia.add = [...newMedias])
                            "
                            :inline="true"
                        />
                    </div>
                </div>

                <div class="row q-ma-md full-width">
                    <div class="col-1">
                        <q-btn
                            @click="isShowSplitDialog = true"
                            :label="$t('Split')"
                            :disable="
                                isLoading ||
                                !isSplittable ||
                                $userCannot('split', 'phases')
                            "
                            color="dark-yellow"
                            class="bg-white"
                            outline
                        >
                        </q-btn>
                        <q-tooltip
                            v-if="!isSplittable"
                            anchor="bottom middle"
                            self="top middle"
                        >
                            {{
                                $t(
                                    'This phase is too short to be split. A phase must be at least be 3 seconds long to split.'
                                )
                            }}
                        </q-tooltip>
                    </div>

                    <div class="col" align="right">
                        <q-btn
                            @click="closeDetail"
                            :label="$t('Cancel')"
                            class="q-mr-sm"
                            color="white"
                            :disable="isLoading"
                            text-color="primary"
                            outline
                        />
                        <q-btn
                            id="save-button"
                            :label="$t('Save')"
                            :disable="
                                $userCannot('edit', 'phases') || !formHasChanged
                            "
                            :loading="isLoading"
                            type="submit"
                            color="primary"
                        />
                    </div>
                </div>
            </q-form>
        </q-card>

        <split-phase-dialog
            v-if="isShowSplitDialog"
            @split="onSplit"
            @close="isShowSplitDialog = false"
            :show="isShowSplitDialog"
            :id="phaseId"
            :start="start"
            :end="end"
            :status="status"
        >
        </split-phase-dialog>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import SplitPhaseDialog from './SplitPhaseDialog.vue';
import { ASSET_STATUS_TO_STYLE, STATUS_OPTIONS } from '@models/assets.js';
import CauseCard from '@components/CauseCard.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import { PROXIMITY_SENSOR_OPTIONS } from '@models/devices.js';
import { isPhaseEditable, isPhaseInProgress } from '@utils/phaseChecker.js';
import Banner from '@components/Banner.vue';
import { editPhaseInfoMessage } from '@utils/getMessages.js';
import MediaInputManager from '@components/MediaInputManager.vue';
import MediaGridManager from '@components/MediaGridManager.vue';
import { updatePhaseMedia } from '@utils/mediaManager.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { useNotificationsStore } from '@stores/notifications.ts';
import { mapActions } from 'pinia';

export default {
    name: 'PhaseDetail',
    components: {
        Banner,
        CauseCard,
        DateTimePicker,
        ProductCard,
        SplitPhaseDialog,
        TableSelect,
        MediaInputManager,
        MediaGridManager
    },
    mixins: [BreadcrumbsManager],
    emits: ['return', 'save'],
    props: {
        assetId: {
            type: [Number, String],
            required: true
        },
        phaseId: {
            type: [Number, String],
            required: true
        },
        phaseUUID: {
            type: String,
            required: true
        },
        start: {
            type: String,
            required: true
        },
        end: {
            type: [String, null],
            required: false,
            default: null
        },
        status: {
            type: String,
            required: true
        },
        causeId: {
            type: [Number, String],
            required: false
        },
        productId: {
            type: [Number, String],
            required: false
        },
        causeName: {
            type: String,
            required: false
        },
        productName: {
            type: String,
            required: false
        },
        description: {
            type: String,
            required: false
        },
        proximityReading: {
            type: [Number, String],
            required: false,
            default: null
        },
        hasProximitySensor: {
            type: Boolean,
            required: false,
            default: false
        },
        isMajor: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Phase Media
         */
        phaseMedia: {
            type: Array,
            required: true
        }
    },
    created() {
        this.initForm();
    },
    data() {
        return {
            ASSET_STATUS_TO_STYLE,
            form: {
                start: null,
                end: null,
                status: null,
                cause: null,
                product: null,
                description: '',
                proximityReading: null
            },
            formRules: {
                cause: [
                    val => this.validateCause(val) || this.$t('Select a cause')
                ],
                product: [
                    val =>
                        this.validateProduct(val) || this.$t('Select a product')
                ]
            },
            statusOptions: STATUS_OPTIONS,
            isShowSplitDialog: false,

            // selectors
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            productSelectProps: {
                url: `/assets/${this.assetId}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            proximitySensorOptions: PROXIMITY_SENSOR_OPTIONS,
            // button loading animation
            isLoading: false,
            editPhaseInfoMessage,
            // media
            editableMedia: [],
            updatedMedia: {
                add: [],
                remove: []
            }
        };
    },
    computed: {
        /**
         * Payload to send server to update phase.
         * @return {Object}
         */
        payload() {
            const status = this.form.status.value;
            const payload = {
                phase: status,
                asset: this.assetId,
                start: this.form.start,
                end: this.form.end,
                description: this.form.description,
                cause: null,
                product: null
            };

            if (status === 'running') {
                payload['product'] = this.form.product.value;
            }

            if (status === 'changeover') {
                payload['cause'] = this.form.cause.value;
                payload['product'] = this.form.product.value;
            }

            if (status === 'in_downtime') {
                if (this.form.cause) {
                    payload['cause'] = this.form.cause.value;
                }
                payload['product'] = this.form.product.value;
                payload['proximity_reading'] =
                    this.form.proximityReading === 'unknown'
                        ? null
                        : this.form.proximityReading;
            }

            if (
                status === 'stopped' &&
                this.form.cause &&
                this.form.cause.value
            ) {
                payload['cause'] = this.form.cause.value;
            }

            return payload;
        },
        /**
         * Determine the cause URL parameters to use.
         * @return {Object}
         */
        causeUrlParams() {
            const urlParams = { ordering: 'name' };
            if (this.form.status.value === 'changeover') {
                urlParams['planned'] = true;
            }
            return urlParams;
        },
        /**
         * Determine whether this phase can be split.
         * @return {Boolean}
         */
        isSplittable() {
            const end = this.end ? new Date(this.end) : new Date();
            return end - new Date(this.start) > 3000;
        },
        /**
         * Enable the submit button when the user updates the form.
         * @return {Boolean}
         */
        formHasChanged() {
            return (
                this.dateToIsoString(this.start) !==
                    this.dateToIsoString(this.form.start) ||
                this.dateToIsoString(this.end) !==
                    this.dateToIsoString(this.form.end) ||
                this.status != this.form.status.value ||
                this.causeId != this.form.cause?.value ||
                this.productId != this.form.product?.value ||
                this.description != this.form.description ||
                this.editableMedia.length !== this.phaseMedia.length ||
                this.updatedMedia.add.length !== 0 ||
                this.updatedMedia.remove.length !== 0
            );
        },
        /**
         * Get query parameters for cause select.
         * In case of stopped phase, only show shutdown causes.
         * @return {Object}
         */
        getCauseQueryParams() {
            const urlParams = { ordering: 'name' };
            if (this.form.status.value === 'stopped') {
                urlParams['type_of_loss'] = 'shutdown';
            }
            return urlParams;
        },
        /**
         * Show a custom message if the selected phase is a stopped phase
         * and no shutdown causes are available.
         * @return {String}
         */
        getNoShutdownCausesMessage() {
            if (this.form.status.value === 'stopped') {
                return this.$t(
                    'No planned shutdown cause is available for this line.'
                );
            }
            return '';
        },
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        }
    },
    methods: {
        isPhaseEditable,
        isPhaseInProgress,
        updatePhaseMedia,
        isFeatureEnabled,
        ...mapActions(useNotificationsStore, ['resolveNotificationAction']),
        /**
         * Validate cause field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateCause(cause) {
            // check whether cause field needs to be validated

            if (this.isMajor && this.form.status.value === 'in_downtime') {
                return Boolean(cause);
            } else if (this.form.status.value === 'changeover') {
                return Boolean(cause);
            } else {
                return true;
            }
        },

        /**
         * Validate product field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateProduct(product) {
            // check whether cause field needs to be validated
            if (this.form.status.value !== 'stopped') {
                return Boolean(product);
            } else {
                return true;
            }
        },
        /**
         * Return to phase timeline on successfull split
         */
        onSplit() {
            this.$emit('return');
        },
        /**
         * Save phase.
         */
        async submit() {
            // Validate product field
            if (!this.validateProduct(this.form.product?.value)) {
                this.$notifyError(
                    this.$t('Please select a product to update this phase.')
                );
                return;
            }

            this.isLoading = true;
            const errorMessage = this.$t(
                'Failed to update phase at this time. Please try again later.'
            );
            try {
                const filesToUpload = this.updatedMedia.add.map(
                    media => media.file
                );
                const mediaIdsToRemove = this.updatedMedia.remove;

                // Upload media
                if (filesToUpload.length > 0 || mediaIdsToRemove.length > 0) {
                    await updatePhaseMedia(
                        filesToUpload,
                        mediaIdsToRemove,
                        this.phaseId
                    );
                }

                // Then update the phase
                await this.$axios.put(
                    `assetphases/${this.phaseId}/`,
                    this.payload
                );

                // If cause is part of payload, resolve the downtime in the store to
                // remove notification from notifications list
                if (this.payload.cause) {
                    this.resolveNotificationAction(this.phaseUUID);
                }

                // notify user
                this.$notifySuccess(this.$t('Updated phase.'));
                // close dialog & reload list
                this.$emit('save');
                this.$emit('return');
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if ('uneditable_phase' in error) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(errorMessage, error);
                }
            } finally {
                this.isLoading = false;
            }
        },
        /**
         * Initialize form.
         */
        initForm() {
            this.form.start = this.start;
            this.form.end = this.end;
            this.form.description = this.description;
            const proximity_reading =
                this.proximityReading === null
                    ? 'unknown'
                    : this.proximityReading;
            this.form.proximityReading = proximity_reading;
            // set status option
            this.form.status = this.statusOptions.filter(
                option => option.value === this.status
            )[0];

            if (this.causeId) {
                this.form.cause = {
                    label: this.causeName,
                    value: this.causeId
                };
            }

            if (this.productId) {
                this.form.product = {
                    label: this.productName,
                    value: this.productId
                };
            }
            // Initialize editable media
            this.editableMedia = this.phaseMedia;
        },
        /**
         * Convert a given date to ISO String
         * @param {Date} date
         * @return {String|null} - ISO String or null if date is invalid
         */
        dateToIsoString(date) {
            if (!date) {
                return null;
            }
            return new Date(date).toISOString();
        },
        /**
         * Close the detail page and return to the timeline.
         */
        closeDetail() {
            this.$emit('return');
        }
    }
};
</script>

<style lang="scss" scoped>
.return-button :deep(.q-btn__wrapper) {
    padding-left: 2px;
    padding-right: 2px;
}

.timeline-panel__form :deep(.q-bar) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}
</style>
